import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { createGame } from "@/lib/stores/unifiedUserStore";
import { Input } from "@/components/ui/InputDefault";
import { Button } from "@/components/ui/button";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface CreateGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGameCreated?: (gameId: string) => void;
}

export default function CreateGameModal({ 
  isOpen, 
  onClose, 
  onGameCreated 
}: CreateGameModalProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany } = useSimplifiedUserData();
  
  const [newGameName, setNewGameName] = useState("");
  const [newGameDescription, setNewGameDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const resetForm = () => {
    setNewGameName("");
    setNewGameDescription("");
  };

  const handleClose = () => {
    if (!isCreating) {
      resetForm();
      onClose();
    }
  };

  const handleCreateGame = async () => {
    if (!newGameName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a game name",
        variant: "destructive",
      });
      return;
    }

    if (!selectedCompany?.id) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const newGame = await createGame({
        name: newGameName.trim(),
        description: newGameDescription.trim(),
        company_id: selectedCompany.id,
        settings: {
          languages: [],
          deadline: '',
          sections: [],
          defaultLanguage: ''
        }
      }, toast);

      resetForm();
      onClose();
      
      // Notify parent component of the newly created game
      if (onGameCreated) {
        onGameCreated(newGame.id);
      }
    } catch (error) {
      console.error("Error creating game:", error);
      // Error handling is done in createGame
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-center">{t('platform.settings.creategamemodal.create_new_game')}</DialogTitle>
          <DialogDescription className="text-center">
            Enter the details for your new game project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label htmlFor="new-game-name" className="block text-sm font-medium text-foreground mb-2">
              Game Name *
            </label>
            <Input
              id="new-game-name"
              type="text"
              value={newGameName}
              onChange={(e) => setNewGameName(e.target.value)}
              placeholder={t('platform.settings.creategamemodal.enter_game_name')}
              className="w-full"
              disabled={isCreating}
            />
          </div>
          <div>
            <label htmlFor="new-game-description" className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              id="new-game-description"
              value={newGameDescription}
              onChange={(e) => setNewGameDescription(e.target.value)}
              placeholder={t('platform.settings.creategamemodal.enter_a_description_for_the_game')}
              rows={3}
              className="w-full px-3 py-2 border border-input rounded shadow-sm bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={isCreating}
            />
          </div>
        </div>
        <DialogFooter className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateGame}
            disabled={isCreating || !newGameName.trim()}
          >
            {isCreating ? "Creating..." : "Create Game"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

